/**
 * BasecampNotesDraftPanel
 * Shown inline on the proposing assistant message after proposeBasecampTodoNotes.
 */

import { useMemo, useRef, useState } from 'react';
import { Send, X, Loader2, CheckCircle2, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { basecampApi } from '@/lib/basecamp-api';
import { looksLikeHtml } from '@/lib/basecamp-html';
import type { BasecampNotesDraft } from '@/lib/basecamp-notes-draft';
import { testIds } from '@/lib/test-ids';
import { toast } from 'sonner';
import { useHydrateBasecampImages } from '../hooks/use-hydrate-basecamp-images';
import { BasecampCommentStyles } from '../styles/basecamp-comment-styles';
import { fixBasecampCommentHtml } from '../utils/comment-html';

export type { BasecampNotesDraft };

interface BasecampNotesDraftPanelProps {
  draft: BasecampNotesDraft;
  onDismiss: () => void;
  onPushed?: () => void;
}

export function BasecampNotesDraftPanel({ draft, onDismiss, onPushed }: BasecampNotesDraftPanelProps) {
  const [isPushing, setIsPushing] = useState(false);
  const [localPushed, setLocalPushed] = useState(draft.status === 'pushed');
  const pushed = localPushed || draft.status === 'pushed';
  const [expanded, setExpanded] = useState(!pushed);
  const previewRef = useRef<HTMLDivElement>(null);
  // Display-only rewrite. Push still sends draft.html so the Basecamp sgid survives.
  const previewHtml = useMemo(() => {
    if (!looksLikeHtml(draft.html)) return null;
    return fixBasecampCommentHtml(draft.html);
  }, [draft.html]);
  useHydrateBasecampImages(previewRef, expanded && previewHtml ? previewHtml : '');

  const handlePush = async () => {
    setIsPushing(true);
    try {
      await basecampApi.pushNotesToBasecamp(draft.todoId, draft.todoUrl, draft.html);
      toast.success('Basecamp notes updated', {
        description: 'The todo description has been updated in Basecamp.',
      });
      setLocalPushed(true);
      setExpanded(false);
      onPushed?.();
    } catch (error) {
      console.error('[BasecampNotesDraftPanel] Push failed:', error);
      toast.error('Failed to push to Basecamp', {
        description: error instanceof Error ? error.message : 'Unknown error',
      });
    } finally {
      setIsPushing(false);
    }
  };

  return (
    <div className="w-full min-w-0 py-2" data-testid={testIds.basecamp.notesDraftPanel}>
      <BasecampCommentStyles />
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-medium text-muted-foreground">
            Basecamp Notes Draft
            {draft.title && (
              <span className="ml-1 text-muted-foreground/60 font-normal">
                - {draft.title.replace(/^Basecamp Notes:\s*/i, '')}
              </span>
            )}
          </span>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              className="h-5 w-5"
              onClick={() => setExpanded((e) => !e)}
              title={expanded ? 'Collapse' : 'Expand'}
            >
              {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-5 w-5"
              onClick={onDismiss}
              title="Dismiss"
            >
              <X className="h-3 w-3" />
            </Button>
          </div>
        </div>

        {expanded && previewHtml && (
          <div className="rounded-lg border border-border bg-muted/30 px-4 py-3 mb-3 max-h-96 overflow-y-auto text-sm">
            <div
              ref={previewRef}
              className="basecamp-todo-description prose prose-sm max-w-none dark:prose-invert [&_h1]:text-sm [&_h1]:font-semibold [&_h1]:text-foreground [&_h1]:mt-3 [&_h1]:mb-1 first:[&_h1]:mt-0 [&_p]:my-1 [&_p]:text-foreground [&_ul]:my-1 [&_li]:my-0 [&_pre]:text-xs [&_pre]:font-mono [&_pre]:text-foreground [&_pre]:bg-muted [&_pre]:border [&_pre]:border-border [&_pre]:p-2 [&_pre]:rounded"
              dangerouslySetInnerHTML={{ __html: previewHtml }}
            />
          </div>
        )}
        {expanded && !previewHtml && draft.html ? (
          <div className="rounded-lg border border-border bg-muted/30 px-4 py-3 mb-3 max-h-56 overflow-y-auto text-sm">
            <p className="text-muted-foreground whitespace-pre-wrap">{draft.html}</p>
          </div>
        ) : null}

        <div className="flex items-center gap-2">
          {pushed ? (
            <div className="flex items-center gap-2 text-sm min-w-0">
              <CheckCircle2 className="h-3.5 w-3.5 text-green-500 shrink-0" />
              <a
                href={draft.todoUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary underline-offset-2 hover:underline break-all"
              >
                {draft.todoUrl}
              </a>
            </div>
          ) : (
            <Button
              variant="default"
              size="sm"
              className="h-7 gap-1.5 text-xs"
              onClick={handlePush}
              disabled={isPushing}
            >
              {isPushing ? (
                <>
                  <Loader2 className="h-3 w-3 animate-spin" />
                  Pushing...
                </>
              ) : (
                <>
                  <Send className="h-3 w-3" />
                  Push to Basecamp
                </>
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
